'use client';

import { useState } from 'react';
import {
  AssessmentResults,
  RiskLevel,
  RiskClassificationResult,
  PrioritizedAction,
  ComplianceGap,
} from '@/lib/eu-ai-act-types';
import {
  getRiskLevelColor,
  getRiskLevelLabel,
  EU_AI_ACT_MILESTONES,
} from '@/lib/eu-ai-act-classifier';

interface Props {
  results: AssessmentResults;
  onViewChecklist: () => void;
  onExport: () => void;
  onStartOver: () => void;
  // Command Center integration
  commandCenterEnabled?: boolean;
  saved?: boolean;
  saving?: boolean;
  saveError?: string | null;
  onSaveToCommandCenter?: () => void;
}

export default function EUAIActResults({
  results,
  onViewChecklist,
  onExport,
  onStartOver,
  commandCenterEnabled = false,
  saved = false,
  saving = false,
  saveError = null,
  onSaveToCommandCenter,
}: Props) {
  const [expandedSystem, setExpandedSystem] = useState<string | null>(null);

  // Count systems by risk level
  const riskCounts = results.systemClassifications.reduce(
    (acc, c) => {
      acc[c.riskLevel] = (acc[c.riskLevel] || 0) + 1;
      return acc;
    },
    {} as Record<RiskLevel, number>
  );

  const hasProhibitedSystems = (riskCounts['unacceptable'] || 0) > 0;
  const hasHighRiskSystems = (riskCounts['high'] || 0) > 0;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-light-text dark:text-foreground mb-2">
          EU AI Act Compliance Assessment
        </h1>
        <p className="text-light-text-secondary dark:text-foreground-secondary">
          Based on your responses, here&apos;s your compliance status
        </p>
      </div>

      {/* Alert for prohibited systems */}
      {hasProhibitedSystems && (
        <div className="bg-red-500/20 border-2 border-red-500 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <span className="text-2xl">⚠️</span>
            <div>
              <h3 className="font-bold text-red-400 text-lg">Prohibited AI Systems Detected</h3>
              <p className="text-red-300">
                You have {riskCounts['unacceptable']} AI system(s) that fall under prohibited categories.
                These must be discontinued immediately as per Article 5 of the EU AI Act.
                The prohibition deadline was <strong>February 2, 2025</strong>.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Compliance Score & Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Compliance Score */}
        <div className="bg-light-surface dark:bg-surface rounded-lg p-6 text-center">
          <div className="text-5xl font-bold text-accent mb-2">
            {results.complianceScore}%
          </div>
          <div className="text-light-text-secondary dark:text-foreground-secondary text-sm">
            Compliance Readiness
          </div>
          <div className="mt-4 h-2 bg-background rounded-full overflow-hidden">
            <div
              className={`h-full transition-all ${
                results.complianceScore >= 80
                  ? 'bg-green-500'
                  : results.complianceScore >= 50
                  ? 'bg-yellow-500'
                  : 'bg-red-500'
              }`}
              style={{ width: `${results.complianceScore}%` }}
            />
          </div>
        </div>

        {/* Risk Distribution */}
        <div className="bg-light-surface dark:bg-surface rounded-lg p-6">
          <h3 className="font-semibold text-light-text dark:text-foreground mb-4">Risk Distribution</h3>
          <div className="space-y-2">
            {(['unacceptable', 'high', 'limited', 'minimal'] as RiskLevel[]).map((level) => (
              <div key={level} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span
                    className={`w-3 h-3 rounded-full ${
                      level === 'unacceptable'
                        ? 'bg-red-500'
                        : level === 'high'
                        ? 'bg-accent'
                        : level === 'limited'
                        ? 'bg-yellow-500'
                        : 'bg-green-500'
                    }`}
                  />
                  <span className="text-sm text-light-text dark:text-foreground">{getRiskLevelLabel(level)}</span>
                </div>
                <span className="font-semibold text-light-text dark:text-foreground">{riskCounts[level] || 0}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Next Deadline */}
        <div className="bg-light-surface dark:bg-surface rounded-lg p-6">
          <h3 className="font-semibold text-light-text dark:text-foreground mb-4">Next Deadline</h3>
          {results.nextDeadline !== 'N/A' ? (
            <>
              <div className="text-2xl font-bold text-accent mb-1">
                {new Date(results.nextDeadline).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </div>
              <p className="text-sm text-light-text-secondary dark:text-foreground-secondary">
                {EU_AI_ACT_MILESTONES.find((m) => m.date === results.nextDeadline)?.title}
              </p>
            </>
          ) : (
            <p className="text-light-text-secondary dark:text-foreground-secondary">No applicable deadlines</p>
          )}
        </div>
      </div>

      {/* System Classifications */}
      <div className="bg-light-surface dark:bg-surface rounded-lg p-6">
        <h3 className="text-xl font-semibold text-light-text dark:text-foreground mb-4">
          AI System Classifications
        </h3>
        <div className="space-y-4">
          {results.systemClassifications.map((classification) => (
            <SystemClassificationCard
              key={classification.system.id}
              classification={classification}
              expanded={expandedSystem === classification.system.id}
              onToggle={() =>
                setExpandedSystem(
                  expandedSystem === classification.system.id ? null : classification.system.id
                )
              }
            />
          ))}
        </div>
      </div>

      {/* Compliance Gaps */}
      {results.complianceGaps.length > 0 && (
        <div className="bg-light-surface dark:bg-surface rounded-lg p-6">
          <h3 className="text-xl font-semibold text-light-text dark:text-foreground mb-4">
            Compliance Gaps ({results.complianceGaps.length})
          </h3>
          <div className="space-y-3">
            {results.complianceGaps.map((gap) => (
              <GapCard key={gap.requirement.id} gap={gap} />
            ))}
          </div>
        </div>
      )}

      {/* Prioritized Actions */}
      <div className="bg-light-surface dark:bg-surface rounded-lg p-6">
        <h3 className="text-xl font-semibold text-light-text dark:text-foreground mb-4">
          Prioritized Action Plan
        </h3>
        {results.prioritizedActions.length > 0 ? (
          <div className="space-y-4">
            {results.prioritizedActions.map((action, index) => (
              <ActionCard key={action.id} action={action} index={index} />
            ))}
          </div>
        ) : (
          <p className="text-light-text-secondary dark:text-foreground-secondary">
            No actions required - you&apos;re in good compliance standing!
          </p>
        )}
      </div>

      {/* Timeline */}
      <div className="bg-light-surface dark:bg-surface rounded-lg p-6">
        <h3 className="text-xl font-semibold text-light-text dark:text-foreground mb-4">
          EU AI Act Timeline
        </h3>
        <div className="relative">
          {EU_AI_ACT_MILESTONES.map((milestone, index) => (
            <div key={milestone.date} className="flex gap-4 mb-6 last:mb-0">
              <div className="flex flex-col items-center">
                <div
                  className={`w-4 h-4 rounded-full ${
                    milestone.isPassed ? 'bg-green-500' : 'bg-accent'
                  }`}
                />
                {index < EU_AI_ACT_MILESTONES.length - 1 && (
                  <div className="w-0.5 h-full bg-cream/20 mt-1" />
                )}
              </div>
              <div className="flex-1 pb-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-light-text dark:text-foreground">{milestone.title}</span>
                  {milestone.isPassed && (
                    <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded">
                      Passed
                    </span>
                  )}
                </div>
                <div className="text-sm text-accent mb-1">
                  {new Date(milestone.date).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </div>
                <p className="text-sm text-light-text-secondary dark:text-foreground-secondary">{milestone.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-4 justify-center">
        <button
          onClick={onViewChecklist}
          className="px-6 py-3 bg-accent text-accent-foreground rounded-lg font-semibold hover:bg-accent-hover transition-colors"
        >
          View Compliance Checklist
        </button>
        <button
          onClick={onExport}
          className="px-6 py-3 bg-light-surface dark:bg-surface text-light-text dark:text-foreground rounded-lg font-semibold border border-light-border dark:border-border hover:bg-light-hover dark:hover:bg-cream/10 transition-colors"
        >
          Export Report
        </button>
        {commandCenterEnabled && !saved && onSaveToCommandCenter && (
          <button
            onClick={onSaveToCommandCenter}
            disabled={saving}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save to Command Center'}
          </button>
        )}
        {saved && (
          <div className="px-6 py-3 bg-green-600/20 text-green-400 rounded-lg font-semibold flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Saved to Command Center
          </div>
        )}
        {saveError && (
          <div className="px-6 py-3 bg-red-600/20 text-red-400 rounded-lg text-sm">
            {saveError}
          </div>
        )}
        <button
          onClick={onStartOver}
          className="px-6 py-3 text-light-text-secondary dark:text-foreground-secondary hover:text-light-text dark:hover:text-foreground transition-colors"
        >
          Start New Assessment
        </button>
      </div>
    </div>
  );
}

function SystemClassificationCard({
  classification,
  expanded,
  onToggle,
}: {
  classification: RiskClassificationResult;
  expanded: boolean;
  onToggle: () => void;
}) {
  const riskColor =
    classification.riskLevel === 'unacceptable'
      ? 'red'
      : classification.riskLevel === 'high'
      ? 'orange'
      : classification.riskLevel === 'limited'
      ? 'yellow'
      : 'green';

  return (
    <div
      className={`border-2 rounded-lg overflow-hidden transition-colors ${
        classification.riskLevel === 'unacceptable'
          ? 'border-red-500'
          : classification.riskLevel === 'high'
          ? 'border-accent'
          : classification.riskLevel === 'limited'
          ? 'border-yellow-500'
          : 'border-green-500'
      }`}
    >
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between bg-background hover:bg-surface transition-colors"
      >
        <div className="flex items-center gap-3">
          <span
            className={`px-2 py-1 rounded text-xs font-semibold ${
              classification.riskLevel === 'unacceptable'
                ? 'bg-red-500/20 text-red-400'
                : classification.riskLevel === 'high'
                ? 'bg-accent-muted text-accent'
                : classification.riskLevel === 'limited'
                ? 'bg-yellow-500/20 text-yellow-400'
                : 'bg-green-500/20 text-green-400'
            }`}
          >
            {getRiskLevelLabel(classification.riskLevel)}
          </span>
          <span className="font-semibold text-light-text dark:text-foreground">{classification.system.name}</span>
        </div>
        <svg
          className={`w-5 h-5 text-light-text-secondary dark:text-foreground-tertiary transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {expanded && (
        <div className="px-4 py-3 bg-background/50 space-y-4">
          <div>
            <h4 className="text-sm font-medium text-light-text-secondary dark:text-foreground-secondary mb-2">Classification Reasons</h4>
            <ul className="space-y-1">
              {classification.reasons.map((reason, i) => (
                <li key={i} className="text-sm text-light-text dark:text-foreground flex items-start gap-2">
                  <span className="text-accent">•</span>
                  {reason}
                </li>
              ))}
            </ul>
          </div>

          {classification.applicableArticles.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-light-text-secondary dark:text-foreground-secondary mb-2">Applicable Articles</h4>
              <div className="flex flex-wrap gap-2">
                {classification.applicableArticles.map((article, i) => (
                  <span key={i} className="px-2 py-1 bg-background rounded text-xs text-light-text-secondary dark:text-foreground-secondary">
                    {article}
                  </span>
                ))}
              </div>
            </div>
          )}

          {classification.deadline !== 'N/A' && (
            <div>
              <h4 className="text-sm font-medium text-light-text-secondary dark:text-foreground-secondary mb-1">Compliance Deadline</h4>
              <span className="text-accent font-semibold">
                {new Date(classification.deadline).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function GapCard({ gap }: { gap: ComplianceGap }) {
  return (
    <div className="flex items-start gap-4 p-4 bg-background rounded-lg">
      <div
        className={`w-3 h-3 rounded-full mt-1 ${
          gap.currentStatus === 'missing' ? 'bg-red-500' : 'bg-yellow-500'
        }`}
      />
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-semibold text-light-text dark:text-foreground">{gap.requirement.title}</span>
          <span className="text-xs px-2 py-0.5 bg-surface text-light-text-secondary dark:text-foreground-tertiary rounded">
            {gap.requirement.article}
          </span>
        </div>
        <p className="text-sm text-light-text-secondary dark:text-foreground-secondary mb-2">{gap.requirement.description}</p>
        <div className="flex items-center gap-4 text-xs">
          <span className={`${gap.currentStatus === 'missing' ? 'text-red-400' : 'text-yellow-400'}`}>
            {gap.currentStatus === 'missing' ? 'Not implemented' : 'Partially implemented'}
          </span>
          <span className="text-light-text-secondary dark:text-foreground-tertiary">
            Effort: {gap.effortEstimate}
          </span>
          <span className="text-light-text-secondary dark:text-foreground-tertiary">
            Affects: {gap.affectedSystems.join(', ')}
          </span>
        </div>
      </div>
    </div>
  );
}

function ActionCard({ action, index }: { action: PrioritizedAction; index: number }) {
  const isUrgent = action.priority <= 2;

  return (
    <div
      className={`p-4 rounded-lg border-l-4 ${
        isUrgent ? 'border-red-500 bg-red-500/10' : 'border-accent bg-background'
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
            isUrgent ? 'bg-red-500 text-white' : 'bg-accent text-accent-foreground'
          }`}
        >
          {index + 1}
        </div>
        <div className="flex-1">
          <h4 className="font-semibold text-light-text dark:text-foreground mb-1">{action.title}</h4>
          <p className="text-sm text-light-text-secondary dark:text-foreground-secondary mb-2">{action.description}</p>
          <div className="flex flex-wrap gap-3 text-xs">
            <span className="px-2 py-1 bg-surface rounded text-light-text-secondary dark:text-foreground-tertiary">
              Deadline: {action.deadline === 'N/A' ? 'N/A' : new Date(action.deadline).toLocaleDateString()}
            </span>
            <span className="px-2 py-1 bg-surface rounded text-light-text-secondary dark:text-foreground-tertiary">
              Effort: {action.estimatedEffort}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
